import React, { useState, useRef, useEffect } from 'react';
import HTMLComment from 'react-html-comment';
import { InputSizeTypes, InputValidationStateTypes } from '../Form.types';
import clsx from 'clsx';
import Typography from '../../Typography/Typography';
import IconStatic from '../../../modules/LocationFinder/IconStatic';

export interface InputLocationSuggestion {
    place_id: string;
    description: string;
    main_text: string;
    secondary_text: string;
}

export interface InputLocationProps extends React.HTMLAttributes<HTMLDivElement> {
    placeholder?: string;
    size?: InputSizeTypes;
    label?: string;
    id: string;
    description?: string;
    disabled?: boolean;
    value?: string;
    validationState?: InputValidationStateTypes;
    validationMessage?: string;
    helperText?: string;
    onValueChange?: (value: string) => void;
    onPlaceSelect?: (place: any) => void;
    onSearch?: (value: string) => void;
    suggestions?: InputLocationSuggestion[];
    loading?: boolean;
    showIcon?: boolean;
    showLocationButton?: boolean;
    onLocationRequest?: () => void;
    /** Accessible name of the "use current location" button; the default keeps existing callers unchanged. */
    locationButtonLabel?: string;
    locationLoading?: boolean;
}

const InputLocation: React.FC<InputLocationProps> = ({
    placeholder = 'z.B. 30161 oder Hannover',
    size = InputSizeTypes.md,
    id,
    label,
    description,
    disabled = false,
    validationState,
    validationMessage,
    helperText,
    value = '',
    onValueChange,
    onPlaceSelect,
    onSearch,
    suggestions = [],
    loading = false,
    showIcon = true,
    showLocationButton = false,
    onLocationRequest,
    locationButtonLabel = 'Use current location',
    locationLoading = false,
    className,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Show/hide dropdown when loading state or suggestions change
    useEffect(() => {
        if (inputValue.length > 2 && (suggestions.length > 0 || loading)) {
            setShowSuggestions(true);
        } else if (!loading && suggestions.length === 0) {
            setShowSuggestions(false);
        }
    }, [loading, suggestions, inputValue]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onValueChange?.(newValue);
        setShowSuggestions(newValue.length > 2 && (suggestions.length > 0 || loading));
        setFocusedIndex(-1);
    };

    const handleInputFocus = () => {
        if (inputValue.length > 2 && (suggestions.length > 0 || loading)) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = (e: React.FocusEvent) => {
        // Delay hiding suggestions to allow for suggestion clicks
        setTimeout(() => {
            if (!suggestionsRef.current?.contains(e.relatedTarget as Node)) {
                setShowSuggestions(false);
                setFocusedIndex(-1);
            }
        }, 150);
    };

    const handleSuggestionClick = (suggestion: InputLocationSuggestion) => {
        setInputValue(suggestion.description);
        setShowSuggestions(false);
        setFocusedIndex(-1);
        onValueChange?.(suggestion.description);
        onPlaceSelect?.(suggestion);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (showSuggestions && focusedIndex >= 0 && focusedIndex < suggestions.length) {
                // Suggestion is focused: select it
                handleSuggestionClick(suggestions[focusedIndex]);
            } else {
                // No suggestion focused: trigger search with current input value
                setShowSuggestions(false);
                onSearch?.(inputValue);
            }
            return;
        }

        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
                break;
            case 'Escape':
                setShowSuggestions(false);
                setFocusedIndex(-1);
                inputRef.current?.blur();
                break;
        }
    };

    return (
        <>
            <HTMLComment text={'@component: InputLocation - Start'} />
            <div {...props} className={clsx(['group flex flex-col gap-2 text-gray-700', validationState, className])}>
                {label && (
                    <label htmlFor={`formfield_${id}`} className={clsx(['group-[.error]:text-system-failure', 'group-[.okay]:text-system-okay'])}>
                        <Typography as={'span'} variant={'micro-copy-medium'} className="uppercase text-darkblue-700">
                            {label}
                        </Typography>
                    </label>
                )}

                <div className="relative">
                    <div className="relative">
                        <input
                            ref={inputRef}
                            type="text"
                            name={'input-location-field'}
                            id={`formfield_${id}`}
                            placeholder={placeholder}
                            value={inputValue}
                            disabled={disabled}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                            autoComplete="off"
                            className={clsx([
                                'w-full transition-all',
                                'text-base leading-9',
                                'rounded-md border border-gray-600',
                                'focus:border-blue-200 focus:outline focus:outline-2 focus:outline-blue-100',
                                'placeholder-darkblue-200 placeholder-opacity-100',
                                'shadow-input',
                                'text-darkblue',
                                'disabled:cursor-not-allowed disabled:text-darkblue-100 disabled:placeholder-darkblue-100 disabled:hover:border-gray-600',
                                'hover:border-darkblue-300',
                                'group-[.error]:border-system-failure group-[.error]:outline group-[.error]:outline-2 group-[.error]:outline-red-200',
                                'group-[.okay]:border-system-okay group-[.okay]:outline group-[.okay]:outline-2 group-[.okay]:outline-green-100',
                                showIcon && !showLocationButton && 'pr-10',
                                showIcon && showLocationButton && 'pr-20',
                                size === InputSizeTypes.sm && 'px-3 py-2',
                                size === InputSizeTypes.md && 'px-4 py-3',
                                size === InputSizeTypes.lg && 'px-4 py-4',
                            ])}
                        />

                        {/* Icons Container */}
                        <div
                            className="absolute right-3 top-1/2 flex -translate-y-1/2 transform items-center gap-2"
                            style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            {/* Location Button */}
                            {showLocationButton && (
                                <button
                                    type="button"
                                    onClick={onLocationRequest}
                                    disabled={disabled || locationLoading}
                                    className="location-button hover:bg-gray-100 flex items-center justify-center rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                    title={locationButtonLabel}
                                    aria-label={locationButtonLabel}
                                >
                                    {locationLoading ? (
                                        <div className="h-4 w-4 animate-spin text-gray-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16">
                                                <path
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M4 12a8 8 0 018-8v0a8 8 0 018 8v0a8 8 0 01-8 8v0a8 8 0 01-8-8"
                                                />
                                            </svg>
                                        </div>
                                    ) : (
                                        <span className="inline-block h-4 w-4 text-center text-sm leading-4 text-darkblue">⦿</span>
                                    )}
                                </button>
                            )}

                            {/* Search Icon */}
                            {showIcon && (
                                <div className="pointer-events-none">
                                    {loading ? (
                                        <div className="animate-spin">
                                            <IconStatic name="Refresh" size="xs" className="text-darkblue-300" />
                                        </div>
                                    ) : (
                                        <IconStatic name="Search" size="xs" className="text-darkblue-300" />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && (suggestions.length > 0 || loading) && (
                        <div
                            ref={suggestionsRef}
                            className="max-h-60 absolute z-50 mt-1 w-full overflow-y-auto rounded-md border border-gray-600 bg-white shadow-lg"
                        >
                            {loading && suggestions.length === 0 && (
                                <div className="flex items-center justify-center gap-2 px-4 py-3">
                                    <div className="animate-spin">
                                        <IconStatic name="Refresh" size="xs" className="text-darkblue-300" />
                                    </div>
                                    <Typography variant="copy-small" className="text-darkblue-300">
                                        Suche...
                                    </Typography>
                                </div>
                            )}
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={suggestion.place_id}
                                    type="button"
                                    className={clsx([
                                        'w-full px-4 py-3 text-left transition-all',
                                        'border-gray-200 border-b last:border-b-0',
                                        'hover:bg-blue-50',
                                        focusedIndex === index && 'bg-blue-100 outline outline-2 outline-blue-200',
                                    ])}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <IconStatic name="Location" size="xs" className="flex-shrink-0 text-darkblue-300" />
                                        <div className="min-w-0 flex-1">
                                            <Typography variant="copy" className="truncate text-darkblue">
                                                {suggestion.main_text}
                                            </Typography>
                                            {suggestion.secondary_text && (
                                                <Typography variant="copy-small" className="truncate text-darkblue-500">
                                                    {suggestion.secondary_text}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Validation Message */}
                {validationState && validationMessage && (
                    <div className={clsx(['flex items-center gap-1', 'group-[.error]:text-system-failure', 'group-[.okay]:text-system-okay'])}>
                        <div>
                            {validationState === 'error' && <IconStatic name="Error" size={'xs'} />}
                            {validationState === 'okay' && <IconStatic name="Check" size={'xs'} />}
                        </div>
                        <Typography variant={'copy-small'}>{validationMessage}</Typography>
                    </div>
                )}

                {/* Helper Text */}
                {helperText && !validationMessage && (
                    <Typography className={'text-darkblue-500'} variant={'copy-small'}>
                        {helperText}
                    </Typography>
                )}

                {/* Description */}
                {description && (
                    <Typography className={'text-gray-700'} variant={'copy-small'}>
                        {description}
                    </Typography>
                )}
            </div>
            <HTMLComment text={'@component: InputLocation - End'} />
        </>
    );
};

export default InputLocation;
